<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Unpause</div>

            <form novalidate @submit.prevent="createScheduleUnpause">
                <LsDatepicker
                    v-model="schedule_at"
                    track-id="unpause_view-schedule_at"
                    name="schedule_at"
                    label="Select date to activate service"
                    class="ls-unpause-view--datepicker"
                    :validation="{ required: true }"
                    :min-date="minDate"
                    :unselectable-days-of-week="unselectableDaysOfWeek"
                />

                <div>
                    <p class="ls-unpause-view--frequency-header">Frequency</p>
                    <p class="ls-unpause-view--frequency-price">{{ frequencyPrice }}</p>
                    <p>{{ pricingDetail }}</p>
                </div>

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="unpause_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="unpause_view-confirm_unpause"
                    text="Confirm Unpause"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsModalService, lsFilters } from 'ls-vue-common';
import gql from 'graphql-tag';
import moment from 'moment-timezone';
import { apolloClient } from '@/vue-apollo';
import { lsHelpers } from '@/helpers';
import { LsButton, LsInput, LsDatepicker } from '@/components/common';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsError from '@/components/common/LsError/LsError.vue';
import { FlowSuccessModal } from '@/modals';
import ScheduleUnpause from '@/graphql/queries/ScheduleUnpause.gql';
import CreateScheduleUnpause from '@/graphql/mutations/CreateScheduleUnpause.gql';

export default {
    name: 'UnpauseView',
    components: {
        LsButton,
        LsInput,
        LsTemplatedLoading,
        LsError,
        LsDatepicker,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
            isInvalid: false,
            minDate: moment()
                .tz('America/Chicago')
                .startOf('day')
                .add(2, 'days')
                .toDate(),

            scheduleUnpause: {},
            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                    }
                `,
            }),

            unselectableDaysOfWeek: [0, 6],

            // form data
            zendesk_ticket_id: '',
            schedule_at: null,
        };
    },
    apollo: {
        scheduleUnpause: {
            query: ScheduleUnpause,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                    schedule_id: _.toNumber(this.$route.params.schedule_id),
                };
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.scheduleUnpause.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
            fetchPolicy: 'network-only',
            result({ data, loading }) {
                if (!loading) {
                    this.unselectableDaysOfWeek = lsHelpers.getUnselectableDaysOfWeekFromDayOfWeekAvailability(
                        data.propertyDayOfWeekAvailability
                    );
                }
            },
        },
    },
    computed: {
        frequencyPrice() {
            return `${lsFilters.frequency({
                cycle: this.scheduleUnpause.cycle,
            })} - ${lsFilters.currency({
                amount: this.scheduleUnpause.total,
            })}`;
        },
        pricingDetail() {
            return lsHelpers.pricingSubLabel({
                price: this.scheduleUnpause.price,
                trust_fee: this.scheduleUnpause.trust_fee,
                tax: this.scheduleUnpause.tax,
            });
        },
    },
    methods: {
        async createScheduleUnpause() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateScheduleUnpause,
                    variables: {
                        customer_id: _.toNumber(this.$route.params.customer_id),
                        property_id: _.toNumber(this.$route.params.property_id),
                        schedule_id: _.toNumber(this.$route.params.schedule_id),
                        schedule_at: moment(this.schedule_at).format('YYYY-MM-DD'),
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Service unpaused for ${this.property.street}`,
                            copy: data.createScheduleUnpause.copy,
                            replaceRoute: true,
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>

<style lang="scss">
.ls-unpause-view--datepicker {
    margin-bottom: 20px !important;
    width: 330px;
}

.ls-unpause-view--frequency-header {
    margin-bottom: 5px;
}

.ls-unpause-view--frequency-price {
    font-weight: $bold;
}
</style>
